/**
 * Topic Normalization Utilities
 *
 * Shared functions for normalizing and matching topic names.
 * Used by hooks and mutation layers to ensure consistent topic lookup.
 */

import type { EntryType, AppDocument } from '@/document'

// ============================================================================
// Normalization
// ============================================================================

/**
 * Normalize topic name for fuzzy matching.
 * Removes punctuation, whitespace, and converts to lowercase.
 * @param name - Topic name to normalize
 * @returns Normalized name for comparison
 */
export function normalizeTopicName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
    .trim()
}

/**
 * Memoized version of normalizeTopicName.
 * Uses a Map cache for repeated lookups on the same names.
 */
const normalizationCache = new Map<string, string>()

export function normalizeTopicNameCached(name: string): string {
  const cached = normalizationCache.get(name)
  if (cached !== undefined) return cached

  const normalized = normalizeTopicName(name)
  normalizationCache.set(name, normalized)
  return normalized
}

// ============================================================================
// Lookup
// ============================================================================

/**
 * Find a topic by normalized name within an entry type scope.
 * @param searchName - Topic name to search for
 * @param topics - Record of topics to search in
 * @param entryType - Optional entry type to scope the search
 * @returns Matching topic or undefined
 */
export function findSimilarTopic(
  searchName: string,
  topics: AppDocument['topics'],
  entryType?: EntryType | null,
) {
  const normalizedSearch = normalizeTopicName(searchName.trim())

  return Object.values(topics).find((topic) => {
    if (topic.archivedAt) return false
    if (entryType !== undefined && topic.entryType !== entryType) return false
    return normalizeTopicName(topic.name) === normalizedSearch
  })
}
